import { motion } from "framer-motion";
import { Smartphone, PlayCircle, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/landingTestIds";

const HERO_PHONE_URL =
  "https://static.prod-images.emergentagent.com/jobs/6a95338f-c56f-4ee5-a4c6-ff921ff7ecc5/images/63e10dced751f41f7d6ab87fc846ac9995f79fdb83fa9e56c109b274d7fea337.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" },
  }),
};

const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#0F172A] pt-36 pb-24 sm:pt-44 sm:pb-32"
    >
      {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[32rem] h-[32rem] bg-[#2563EB]/25 rounded-full blur-[140px]" />
        <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] bg-[#14b8a6]/15 rounded-full blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(#94A3B8 1px, transparent 1px), linear-gradient(90deg, #94A3B8 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-1.5 mb-8"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#38bdf8]" />
            <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#93c5fd]">
              AI Powered Real Estate CRM
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]"
          >
            Goodbye Paper Diaries,
            <br />
            <span className="bg-gradient-to-r from-[#38bdf8] to-[#2dd4bf] bg-clip-text text-transparent">
              Hello Smart Automation
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
          >
            Tracre One is the smart Android CRM built for real estate agents, brokers &
            consultants. Manage leads, automate WhatsApp follow-ups with AI, and
            run your entire business from your pocket — no diary required.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid={LANDING.heroDownloadBtn}
              size="lg"
              onClick={() =>
                window.open("#", "_blank", "noopener,noreferrer")
              }
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-14 px-8 text-base font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all duration-300 gap-2.5"
            >
              <Smartphone className="w-5 h-5" />
              Download Android App
            </Button>
            <Button
              data-testid={LANDING.heroViewFeaturesBtn}
              size="lg"
              variant="outline"
              onClick={() =>
                document.querySelector("#features")?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-transparent hover:bg-white/10 text-white border-slate-500 rounded-lg h-14 px-8 text-base font-semibold gap-2.5 transition-all duration-300"
            >
              <PlayCircle className="w-5 h-5" />
              See Features
            </Button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
          >
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <ShieldCheck className="w-4 h-4 text-[#2dd4bf]" />
              Secure Local Storage
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Sparkles className="w-4 h-4 text-[#2dd4bf]" />
              AI Co-pilot
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <TrendingUp className="w-4 h-4 text-[#2dd4bf]" />
              Built to Grow Your Business 10x
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          className="relative flex justify-center lg:justify-end"
        >
          <div className="absolute w-72 h-72 sm:w-96 sm:h-96 bg-[#2563EB]/30 rounded-full blur-[100px]" />
          <img
            data-testid={LANDING.heroPhoneMockup}
            src={HERO_PHONE_URL}
            alt="Tracre One CRM dashboard on Android phone"
            className="relative w-64 sm:w-80 lg:w-96 rounded-3xl shadow-2xl ring-1 ring-white/10 drop-shadow-2xl animate-[float_6s_ease-in-out_infinite]"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
